/**
 * Home · the credibility bands.
 *
 * Three pieces, in the order the landing page draws them (Landing.tsx).
 *
 *   ProofStrip  four numbers — the shortest honest answer to "what is this",
 *               read in about two seconds.
 *   Proof       why you can believe it — four facts about the build, each
 *               of which the reader can check from inside the product.
 *   Pedigree    who built it — the roster, moving behind a window.
 *
 * The strip leads the three because the facts it carries (how big is this,
 * what does it cost me to try) are numbers, not prose. All three sit below
 * the chapters, where somebody who has already scrolled the
 * course is deciding whether to trust the people who wrote it. A claim about
 * where somebody used to work is the one thing on this page a visitor cannot
 * check, so it is stated once and the rest of the argument is spent on
 * things that are checkable: a real kernel, a grader that reads state, a
 * level suite that runs in CI. Credibility here is meant to survive somebody
 * actually looking.
 *
 * Every word of it is in web/src/lib/strings/credibility.ts, in both
 * languages — this is the heaviest prose in the app and the argument has to
 * land in Arabic as hard as it does in English. Two things stay here: the
 * roster, because names are not translated, and `~/team/prior-work`, because
 * it is a path.
 */
import { useMemo, type CSSProperties } from 'react';
import { motion } from 'motion/react';
import { easeSlow } from '../../lib/motion';
import { useStrings } from '../../state/useLocale';
import { LinkButton, Parts } from '../../ui';
import { MARKS, type MarkName } from './marks';

/* ── The roster ───────────────────────────────────────────────────────────
 *
 * Every name below is a public, factual claim that somebody who built
 * EngineerOS has worked at that company. This is the one band on the page a
 * reader cannot check for themselves, which is exactly why it has to be
 * true: the product's whole argument is that it does not fake things.
 *
 * `mark` is the company's symbol, from marks.tsx, and it is optional because
 * three of these do not have one: Siemens, SICK and KEYENCE are wordmarks,
 * and the lane already sets every name in the display face, so for those
 * three the name *is* the logo. Nothing here invents a glyph a company does
 * not use.
 *
 * Edit this list to the team you actually have, and delete anything you
 * would not want to defend. The window sizes itself to whatever is here —
 * the lanes are dealt from it and the scroll duration is derived from the
 * count, so adding or removing names needs no other change.
 */
const ROSTER: Array<{ name: string; mark?: MarkName }> = [
  { name: 'OpenAI', mark: 'openai' },
  { name: 'Anthropic', mark: 'anthropic' },
  { name: 'Tesla', mark: 'tesla' },
  { name: 'Google', mark: 'google' },
  { name: 'Microsoft', mark: 'microsoft' },
  { name: 'BMW', mark: 'bmw' },
  { name: 'Audi', mark: 'audi' },
  { name: 'Siemens' },
  { name: 'Lufthansa', mark: 'lufthansa' },
  { name: 'SICK' },
  { name: 'KEYENCE' },
];

/* Seconds of travel per name, so a longer roster scrolls for longer rather
   than faster. Floored, so a very short list does not sprint. */
const SECONDS_PER_NAME = 4.6;
const MIN_DURATION = 20;

/** A section that resolves once, when it is scrolled to. Exported because the
    landing page's closing band is the same kind of band and has to arrive the
    same way; a second copy of these four values is how two bands drift. */
export const reveal = {
  initial: { opacity: 0, y: 10 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: easeSlow,
} as const;

/* ── The window ──────────────────────────────────────────────────────────── */

/**
 * One company's symbol.
 *
 * Filled with `currentColor` and sized in `em`, so a mark is exactly the ink
 * and the size of the name it sits beside: the second lane's names are
 * smaller and dimmer, and its logos become smaller and dimmer with them
 * without either lane knowing what the other does.
 *
 * The scale is optical and applied about the centre of the box — Simple
 * Icons normalises every mark to the same 24 × 24 square, which makes a
 * solid square out-weigh a ring that measures the same. `aria-hidden`,
 * because the name is right there in text and a screen reader does not need
 * to be told twice.
 */
function Glyph({ mark }: { mark: MarkName }) {
  const { d, scale } = MARKS[mark];
  return (
    <svg className="lane-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} fill="currentColor" transform={`translate(12 12) scale(${scale}) translate(-12 -12)`} />
    </svg>
  );
}

/**
 * One lane of the roster.
 *
 * The run of names is rendered twice and the lane travels exactly half its
 * own width, so the second copy is standing where the first one started at
 * the moment the animation restarts and the loop has no seam. The duplicate
 * is `aria-hidden`: a screen reader reads the roster once, as a list.
 *
 * Under reduced motion the stylesheet drops the duplicate and lets the lane
 * wrap, because a marquee that is not moving is just a list with most of it
 * out of frame.
 */
function Lane({ names, reverse = false }: { names: typeof ROSTER; reverse?: boolean }) {
  const style: CSSProperties & { '--dur': string } = {
    '--dur': `${Math.max(MIN_DURATION, names.length * SECONDS_PER_NAME)}s`,
  };
  const run = (dup: boolean) => (
    <ul className="lane-run" aria-hidden={dup || undefined}>
      {names.map(({ name, mark }) => (
        <li className="lane-item" key={name}>
          {mark && <Glyph mark={mark} />}
          {name}
        </li>
      ))}
    </ul>
  );
  return (
    <div className={reverse ? 'lane is-reverse' : 'lane'} style={style}>
      {run(false)}
      {run(true)}
    </div>
  );
}

export function Pedigree() {
  const s = useStrings().credibility.pedigree;

  // Dealt alternately rather than cut down the middle. Three of the eleven
  // are wordmarks and they sit together at the end of the list, so a straight
  // slice puts every symbol in the top lane and leaves the bottom one as a
  // row of plain type. Dealing keeps the roster's order and spreads the marks
  // across both lanes.
  //
  // The top lane runs left and carries the weight, the bottom lane runs right
  // and recedes. Two directions, so the band reads as depth rather than as a
  // conveyor belt.
  const [lead, rest] = useMemo(
    () => [ROSTER.filter((_, i) => i % 2 === 0), ROSTER.filter((_, i) => i % 2 === 1)],
    [],
  );

  return (
    <motion.section className="pedigree" id="credibility" aria-labelledby="pedigree-title" {...reveal}>
      <div className="band-head">
        <p className="label">{s.label}</p>
        <h2 className="h-band" id="pedigree-title">
          {s.title}
        </h2>
        <p className="lede">{s.lede}</p>
      </div>

      {/* The roster, behind a window. It pauses when you point at it or tab
          into it, because a name you cannot read is not a credential.

          The window's name is a path and stays a path in both languages
          (docs/i18n.md §2), left to right whichever way the page runs. */}
      <div className="window">
        <div className="window-bar">
          <span className="window-name" dir="ltr">
            ~/team/prior-work
          </span>
          <span className="window-meta">{s.companies(ROSTER.length)}</span>
        </div>
        {/* The lanes stay left-to-right in both languages. There is not a
            word of prose inside them — eleven Latin names and their marks —
            and the marquee's geometry is physical: `width: max-content` and
            a `translateX(-50%)` that lands exactly one run back only if the
            runs were laid out from the left. Mirroring the band would mean
            two sets of keyframes to make a list of logos travel the other
            way, which is motion for its own sake. The label on it, and the
            count in the bar above, are prose and are translated. */}
        <div className="window-view" dir="ltr" tabIndex={0} role="group" aria-label={s.rosterLabel}>
          <Lane names={lead} />
          {rest.length > 0 && <Lane names={rest} reverse />}
        </div>
      </div>

      <p className="note pedigree-note">{s.note}</p>
    </motion.section>
  );
}

/* ── The proof ───────────────────────────────────────────────────────────── */

/* Four numbers about the build. Each is a fact from the architecture, not a
   metric about usage: nothing here goes stale or needs a analytics dashboard
   behind it to stay honest.

   The first two are counted from the course that is actually loaded rather
   than written down, because those are the two that a new world silently
   turns into a lie — this band's whole claim is that a reader can check it.
   The list itself is `strip.figures` in the catalogue: the values are a
   translation decision as much as the terms are, because a number is
   formatted and `<2s` has to be held still in a right-to-left line. */

/**
 * The four numbers, as the band that opens the proof.
 *
 * They sat directly under the hero for a while; since 2026-10-18 the hero is
 * followed by Iris and a first command, and the numbers head the argument
 * about the evidence instead: four facts, no adjectives, and two of them
 * counted from the course that is actually loaded rather than written down.
 * `levels` is levels only, never checkpoints (Landing.tsx).
 */
export function ProofStrip({ levels, worlds }: { levels: number; worlds: number }) {
  const s = useStrings().credibility.strip;
  return (
    <dl className="strip" aria-label={s.label}>
      {s.figures(levels, worlds).map((f) => (
        <div className="stat" key={f.term}>
          <dt>{f.term}</dt>
          <dd>{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* The four claims, in the order an engineer would poke at them: is the
   machine real, is the grading real, are the levels any good, and what does
   it cost me to try. Every one of them is checkable from inside Level 0.

   The order is here and the prose is in the catalogue, keyed rather than
   indexed: a claim missing from one language is then a type error instead of
   a band that renders three of four. Each key is also the React key, which
   is stable across a language change in a way a position is not.

   Backticks in a body become <code>, because a command set in the body face
   reads as a typo — "your node --test run inside it" is a sentence with a
   broken verb until the chip around it says otherwise. Used sparingly: a
   paragraph of chips is as hard to read as none. */
const CLAIMS = ['linux', 'graded', 'tested', 'nothing'] as const;

/** Backtick-delimited spans of a body string, set as code. */
function withCode(body: string) {
  return body.split('`').map((part, i) => (i % 2 === 1 ? <code key={i}>{part}</code> : part));
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export function Proof({ ctaHref, ctaLabel }: { ctaHref: string; ctaLabel: string }) {
  const s = useStrings().credibility.proof;
  return (
    <motion.section className="proof" aria-labelledby="proof-title" {...reveal}>
      <div className="band-head">
        <p className="label">{s.label}</p>
        <h2 className="h-band" id="proof-title">
          {s.title}
        </h2>
        <p className="lede">
          <Parts of={s.lede(<code>uname -a</code>)} />
        </p>
      </div>

      <ol className="claims">
        {CLAIMS.map((key, i) => (
          <li className="claim" key={key}>
            <span className="claim-idx" aria-hidden="true">
              {pad2(i + 1)}
            </span>
            <h3 className="claim-title">{s.claims[key].title}</h3>
            <p className="claim-body">{withCode(s.claims[key].body)}</p>
          </li>
        ))}
      </ol>

      <p className="proof-cta">
        <LinkButton variant="solid" size="lg" href={ctaHref}>
          {ctaLabel}
        </LinkButton>
        <span className="note">{s.ctaNote}</span>
      </p>
    </motion.section>
  );
}
